import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, isOfflineApiError, providerApi, type FoodItemRecord } from "../../lib/api";
import { readCachedApiResponse, writeCachedApiResponse } from "../../lib/offline-store";
import { useAppPreferences } from "../../lib/preferences";
import { subscribeRealtimeEvent } from "../../lib/realtime";

export function foodMenuCachePath(venueId: string) {
  return `/api/v1/provider/venues/${venueId}/food`;
}

/** The venue's own menu, with stock. Orders change stock, so it refreshes on order events. */
export function useVenueMenu(venueId: string | null | undefined) {
  const { t } = useAppPreferences();
  const [items, setItems] = useState<FoodItemRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const requestRef = useRef(0);

  const persist = useCallback((next: FoodItemRecord[]) => {
    setItems(next);
    if (venueId) writeCachedApiResponse(foodMenuCachePath(venueId), { items: next });
  }, [venueId]);

  const reload = useCallback(async () => {
    if (!venueId) {
      setItems([]);
      return;
    }
    const requestId = ++requestRef.current;
    setIsLoading(true);
    try {
      const response = await providerApi.listFood(venueId);
      if (requestId !== requestRef.current) return;
      persist(response.items);
      setError(null);
      setIsCached(false);
    } catch (requestError) {
      if (requestId !== requestRef.current) return;
      const cached = isOfflineApiError(requestError)
        ? readCachedApiResponse<{ items: FoodItemRecord[] }>(foodMenuCachePath(venueId))
        : null;
      if (cached) {
        setItems(cached.items);
        setIsCached(true);
        setError(null);
      } else {
        setError(requestError instanceof ApiError && requestError.status !== 0
          ? requestError.detail
          : t("Could not load the menu."));
      }
    } finally {
      if (requestId === requestRef.current) setIsLoading(false);
    }
  }, [persist, t, venueId]);

  useEffect(() => {
    setItems([]);
    void reload();
  }, [reload]);

  useEffect(() => subscribeRealtimeEvent((event) => {
    if ((event.type === "food_order.created" || event.type === "food_order.updated")
      && event.payload?.venue_id === venueId) {
      void reload();
    }
  }), [reload, venueId]);

  const replaceItem = useCallback((item: FoodItemRecord) => {
    setItems((current) => {
      const next = current.some((existing) => existing.id === item.id)
        ? current.map((existing) => (existing.id === item.id ? item : existing))
        : [...current, item];
      if (venueId) writeCachedApiResponse(foodMenuCachePath(venueId), { items: next });
      return next;
    });
  }, [venueId]);

  return { items, isLoading, error, isCached, reload, persist, replaceItem };
}

export function stockLabel(item: FoodItemRecord, t: (key: string, values?: Record<string, string | number>) => string) {
  if (!item.track_stock) return null;
  if (item.sold_out) return t("Sold out");
  return t("{count} left", { count: item.stock_quantity ?? 0 });
}
